import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  CalendarDaysIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const mockEmployees = [
  { id: 1, name: 'Aditya Verma', department: 'Technology', manager: 'Sanjay Kumar', lastReview: '2025-06-15', rating: 'Outstanding' },
  { id: 2, name: 'Pooja Singh', department: 'Product', manager: 'Geeta Patel', lastReview: '2025-07-20', rating: 'Meets Expectations' },
  { id: 3, name: 'Kunal Kapoor', department: 'Marketing', manager: 'Vivek Sharma', lastReview: '2025-08-01', rating: 'Needs Improvement' },
  { id: 4, name: 'Neha Reddy', department: 'Technology', manager: 'Sanjay Kumar', lastReview: '2025-09-05', rating: 'Exceeds Expectations' },
  { id: 5, name: 'Anjali Desai', department: 'Human Resources', manager: 'Meera Kulkarni', lastReview: '2025-07-28', rating: 'Meets Expectations' },
];

const mockReviews = [
  { id: 1, employee: 'Aditya Verma', date: '2025-10-15', type: 'Quarterly Review' },
  { id: 2, employee: 'Kunal Kapoor', date: '2025-10-22', type: 'PIP Review' },
  { id: 3, employee: 'Neha Reddy', date: '2025-11-05', type: 'Annual Review' },
];
// --- END: MOCK DATA ---

const PerformanceDashboard = () => {
  const [employees, setEmployees] = useState(mockEmployees);
  const [searchTerm, setSearchTerm] = useState('');

  const averageRating = useMemo(() => {
    const ratings = employees.map(e => e.rating);
    const scoreMap = {
      'Outstanding': 5,
      'Exceeds Expectations': 4,
      'Meets Expectations': 3,
      'Needs Improvement': 2,
      'Not Rated': 1,
    };
    const totalScore = ratings.reduce((sum, rating) => sum + (scoreMap[rating] || 0), 0);
    return (totalScore / ratings.length).toFixed(2);
  }, [employees]);

  const upcomingReviewsCount = mockReviews.length;
  const employeesOnPIP = employees.filter(e => e.rating === 'Needs Improvement').length;

  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    return employees.filter(emp =>
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.manager.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, employees]);

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Outstanding':
        return 'bg-green-100 text-green-600';
      case 'Exceeds Expectations':
        return 'bg-blue-100 text-blue-600';
      case 'Meets Expectations':
        return 'bg-yellow-100 text-yellow-600';
      case 'Needs Improvement':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-extrabold text-blue-800">Performance Dashboard</h1>
        <p className="text-xl font-light text-blue-600 mt-2">
          Monitor and manage employee performance and reviews.
        </p>
      </motion.div>

      {/* Dashboard Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-7xl mx-auto">
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-blue-100">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{averageRating}</h3>
              <p className="text-sm text-gray-500">Avg. Performance Score</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-cyan-100">
              <CalendarDaysIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{upcomingReviewsCount}</h3>
              <p className="text-sm text-gray-500">Upcoming Reviews</p>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
          <div className="bg-white p-6 rounded-3xl shadow-lg flex items-center space-x-4">
            <div className="p-3 rounded-full bg-red-100">
              <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-gray-900">{employeesOnPIP}</h3>
              <p className="text-sm text-gray-500">Employees on PIP</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content: Employee Table & Review List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Employee Performance Table */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Employee Performance</h3>
            <button className="py-2 px-4 rounded-full bg-blue-600 text-white flex items-center shadow-lg hover:bg-blue-700 transition-colors">
              <PlusIcon className="h-5 w-5 mr-2" />
              New Review
            </button>
          </div>
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee, department or manager..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Review</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp) => (
                      <motion.tr
                        key={emp.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                          <UserCircleIcon className="h-6 w-6 mr-2 text-gray-400" />
                          {emp.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{emp.manager}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(emp.lastReview).format('MMM D, YYYY')}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRatingColor(emp.rating)}`}>
                            {emp.rating}
                          </span>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-10 text-center text-gray-500">
                        No matching employees found.
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Upcoming Reviews */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.5 }}
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Upcoming Reviews</h3>
          <ul className="divide-y divide-gray-200">
            {mockReviews.map((review) => (
              <li key={review.id} className="py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{review.employee}</p>
                  <p className="text-xs text-gray-500">{review.type}</p>
                </div>
                <div className="flex items-center text-right">
                  <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-1" />
                  <span className="text-sm font-medium text-gray-700">{moment(review.date).format('MMM D, YYYY')}</span>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;